import React, { ReactNode } from "react";
import cn from "classnames";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import CustomSelect from "../CustomSelect";
import type { DataColumn, SortState } from ".";
import styles from "./cards.module.scss";

export type CardColumn<T> = Pick<
  DataColumn<T>,
  "key" | "title" | "render" | "fixed" | "mobile"
>;

interface DataCardsProps<T> {
  columns: CardColumn<T>[];
  /** The rows to draw, already sorted and paged. */
  rows: T[];
  rowKey: (row: T, index: number) => React.Key;
  emptyText?: string;
  /** Cells of the Totals card by column key. */
  totals?: Record<string, ReactNode>;
  /** Keys that lead the card body, in this order; the rest follow. */
  order?: string[];
  sort?: SortState;
  /** Without it the cards have no sort control. */
  onSortChange?: (sort: SortState) => void;
  sortable?: { key: string; title: string }[];
  /** Ref for an empty element after the last card (infinite scroll). */
  sentinelRef?: (node: HTMLElement | null) => void;
}

/**
 * A table on a phone: a card per row. The title column (an explicit
 * mobile "title", else the pinned one, else the first) heads the card with
 * the "header" cells beside it; "full" cells take a line each, the rest
 * share a grid of label / value pairs, and "footer" cells (actions) close
 * the card without a label.
 */
function DataCards<T>({
  columns,
  rows,
  rowKey,
  emptyText = "No data",
  totals,
  order = [],
  sort = null,
  onSortChange,
  sortable = [],
  sentinelRef,
}: DataCardsProps<T>) {
  const titleColumn =
    columns.find((column) => column.mobile === "title") ||
    columns.find((column) => column.fixed) ||
    columns[0];
  const rest = columns.filter(
    (column) => column !== titleColumn && column.mobile !== "hidden",
  );
  const rank = (column: CardColumn<T>) => {
    const index = order.indexOf(column.key);
    return index < 0 ? order.length : index;
  };
  const body = rest
    .filter((column) => !column.mobile || column.mobile === "full")
    // Array#sort is stable: unlisted columns keep their own order.
    .sort((a, b) => rank(a) - rank(b));
  const full = body.filter((column) => column.mobile === "full");
  const compact = body.filter((column) => !column.mobile);
  const header = rest.filter((column) => column.mobile === "header");
  const footer = rest.filter((column) => column.mobile === "footer");

  const totalCells = totals
    ? columns.filter(
        (column) => column !== titleColumn && totals[column.key] != null,
      )
    : [];

  const field = (column: CardColumn<T>, content: ReactNode, wide = false) => (
    <div key={column.key} className={cn(styles.field, { [styles.full]: wide })}>
      <span className={styles.label}>{column.title}</span>
      <span className={styles.value}>
        {content == null || content === "" ? "—" : content}
      </span>
    </div>
  );

  return (
    <div className={styles.cards}>
      {onSortChange && sortable.length > 0 && rows.length > 1 && (
        <div className={styles.sortRow}>
          <CustomSelect
            className={styles.sortSelect}
            aria-label="Sort by"
            placeholder="Sort by"
            value={sort?.key ?? ""}
            options={sortable.map((column) => ({
              value: column.key,
              label: `Sort by ${column.title}`,
            }))}
            onChange={(key) =>
              onSortChange({
                key,
                direction: sort?.key === key ? sort.direction : "asc",
              })
            }
          />
          <button
            type="button"
            className={styles.directionButton}
            disabled={!sort}
            onClick={() =>
              sort &&
              onSortChange({
                ...sort,
                direction: sort.direction === "asc" ? "desc" : "asc",
              })
            }
            aria-label={
              sort?.direction === "desc"
                ? "Sorted descending"
                : "Sorted ascending"
            }
          >
            {sort?.direction === "desc" ? (
              <ArrowDownOutlined />
            ) : (
              <ArrowUpOutlined />
            )}
          </button>
        </div>
      )}

      {totals && totalCells.length > 0 && rows.length > 0 && (
        <div className={styles.totals}>
          <span className={styles.totalsTitle}>
            {totals[titleColumn.key] ?? "Totals"}
          </span>
          <div className={styles.grid}>
            {totalCells.map((column) => field(column, totals[column.key]))}
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className={styles.empty}>{emptyText}</p>
      ) : (
        <div className={styles.list} role="list">
          {rows.map((row, index) => (
            <div
              key={rowKey(row, index)}
              className={styles.card}
              role="listitem"
            >
              <div className={styles.cardHeader}>
                <span className={styles.title}>
                  {titleColumn.mobile !== "title" && (
                    <span className={styles.titleLabel}>
                      {titleColumn.title}
                    </span>
                  )}
                  {titleColumn.render(row)}
                </span>
                {header.length > 0 && (
                  <span className={styles.headerCells}>
                    {header.map((column) => (
                      <React.Fragment key={column.key}>
                        {column.render(row)}
                      </React.Fragment>
                    ))}
                  </span>
                )}
              </div>
              {(full.length > 0 || compact.length > 0) && (
                <div className={styles.grid}>
                  {full.map((column) =>
                    field(column, column.render(row), true),
                  )}
                  {compact.map((column) => field(column, column.render(row)))}
                </div>
              )}
              {footer.length > 0 && (
                <div className={styles.footer}>
                  {footer.map((column) => (
                    <React.Fragment key={column.key}>
                      {column.render(row)}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {sentinelRef && <div ref={sentinelRef} aria-hidden />}
    </div>
  );
}

export default DataCards;
